import { useState, useEffect, useRef } from 'react';
import JsonEditor from './JsonEditor';
import UrlDisplay from './UrlDisplay';
import { useAuth } from '../contexts/AuthContext';
import { DYNAMIC_API_URL } from '../config/api';

type RequestPanelProps = {
  collections: any[];
  setCollections: (cols: any[]) => void;
  openTabs: Array<{ id: string; collectionId: number; requestId: number }>;
  setOpenTabs: (tabs: Array<{ id: string; collectionId: number; requestId: number }>) => void;
  activeTabId: string | null;
  setActiveTabId: (id: string | null) => void;
  showDemo?: boolean;
  isAuthenticated?: boolean;
};

export default function RequestPanel({ collections, setCollections, openTabs, setOpenTabs, activeTabId, setActiveTabId, showDemo = false, isAuthenticated = false }: RequestPanelProps) {
  const [showMethodDropdown, setShowMethodDropdown] = useState(false);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [requestIds, setRequestIds] = useState<Record<string, string>>({});
  const [requestBodies, setRequestBodies] = useState<Record<string, string>>({});
  const [activeInnerTab, setActiveInnerTab] = useState<'ID' | 'Body'>('Body');
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [activeCodeTab, setActiveCodeTab] = useState<'curl' | 'js' | 'nodejs' | 'python'>('curl');
  const methodDropdownRef = useRef<HTMLDivElement>(null);
  const { apiKey } = useAuth();


  // Закрытие дропдауна при клике вне
  useEffect(() => {
    if (!showMethodDropdown) return;
    function handleClick(e: MouseEvent) {
      if (
        methodDropdownRef.current &&
        !methodDropdownRef.current.contains(e.target as Node)
      ) {
        setShowMethodDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMethodDropdown]);

  const closeTab = (tabId: string) => {
    const newTabs = openTabs.filter(tab => tab.id !== tabId);
    setOpenTabs(newTabs);
    
    // Если закрываем активный таб, переключаемся на последний
    if (activeTabId === tabId) {
      setActiveTabId(newTabs.length > 0 ? newTabs[newTabs.length - 1].id : null);
    }
  };

  // Обновить коллекцию
  const updateCollection = (collectionId: number, updates: { name?: string }) => {
    setCollections(collections.map((col, idx) => 
      idx === collectionId ? { ...col, ...updates } : col
    ));
  };

  // Обновить запрос в коллекции
  const updateRequest = (collectionId: number, requestId: number, updates: { method?: string; desc?: string; path?: string; body?: string; recordId?: string }) => {
    setCollections(collections.map((col, idx) => {
      if (idx === collectionId) {
        return {
          ...col,
          items: col.items.map((item: any, itemIdx: number) => 
            itemIdx === requestId ? { ...item, ...updates } : item
          )
        };
      }
      return col;
    }));
    
    // При смене метода на GET автоматически переключаем на вкладку ID
    if (updates.method === 'GET') {
      setActiveInnerTab('ID');
    }
  };

  // Получаем активный таб
  const activeTab = openTabs.find(tab => tab.id === activeTabId);
  const activeCollection = activeTab ? collections[activeTab.collectionId] : null;
  const activeRequest = activeCollection?.items[activeTab?.requestId ?? 0];
  
  // Установка ID и Body при переключении между запросами
  useEffect(() => {
    if (!activeTabId || !activeTab || !activeRequest) return;
    
    // Инициализация ID для демо-режима
    if (showDemo && activeTab.requestId === 1) {
      // Для второго запроса в демо-режиме предустановить ID
      setRequestIds(prev => {
        if (!prev[activeTabId]) {
          return { ...prev, [activeTabId]: '507f1f77bcf86cd799439011' };
        }
        return prev;
      });
    }
    
    // Инициализация Body для демо-режима
    if (showDemo) {
      setRequestBodies(prev => {
        if (!prev[activeTabId]) {
          const requestIndex = activeTab.requestId ?? 0;
          let defaultBody = '{}';
          if (requestIndex === 1 || activeRequest.desc?.toLowerCase().includes('make-task-done')) {
            defaultBody = '{\n  "name": "use Supaserver today",\n  "isDone": true\n}';
          } else if (requestIndex === 0) {
            defaultBody = '{\n  "name": "use Supaserver today",\n  "isDone": false\n}';
          }
          return { ...prev, [activeTabId]: defaultBody };
        }
        return prev;
      });
    }
    
    // Устанавливаем активную вкладку в зависимости от метода
    // Для GET по умолчанию показываем ID, для остальных - Body
    if (activeRequest.method === 'GET') {
      setActiveInnerTab('ID');
    } else {
      setActiveInnerTab('Body');
    }
  }, [activeTabId, showDemo, activeTab, activeRequest]);

  // Получаем значения для текущего запроса (из коллекции или локального состояния для обратной совместимости)
  const responseText = activeTabId ? responses[activeTabId] || '' : '';
  // Приоритет: значение из коллекции, затем локальное состояние, затем дефолт
  const requestId = activeRequest?.recordId ?? (activeTabId ? requestIds[activeTabId] || '' : '');
  const requestBody = activeRequest?.body ?? (activeTabId ? requestBodies[activeTabId] || '{}' : '{}');

  // Логика для демо-режима: автоматически устанавливаем методы для запросов
  useEffect(() => {
    if (!showDemo || !activeTab) return;
    
    const activeCollection = collections[activeTab.collectionId];
    const activeRequest = activeCollection?.items[activeTab.requestId];
    
    if (!activeRequest) return;
    
    // Первый запрос должен быть PUT (create-task)
    if (activeTab.requestId === 0 && activeRequest.method !== 'PUT') {
      updateRequest(activeTab.collectionId, activeTab.requestId, { method: 'PUT' });
    }
    
    // Второй запрос должен быть POST (make-task-done)
    if (activeTab.requestId === 1 && activeRequest.method !== 'POST') {
      const requestName = activeRequest.desc?.toLowerCase() || '';
      if (requestName.includes('make-task-done')) {
        updateRequest(activeTab.collectionId, activeTab.requestId, { method: 'POST' });
      }
    }
  }, [showDemo, activeTab, collections, updateRequest]);

  // Создание дефолтного таба только для аутентифицированных пользователей БЕЗ демо
  // В демо-режиме коллекции создаются через ElementHighlighter
  useEffect(() => {
    // Не создаём дефолтные коллекции для демо-режима или неаутентифицированных
    if (showDemo || !isAuthenticated) return;
    
    if (openTabs.length === 0 && collections.length > 0) {
      // Если есть коллекции, проверяем, есть ли в первой коллекции запросы
      const firstCollection = collections[0];
      if (firstCollection && firstCollection.items.length > 0) {
        // Создаем таб для первого запроса
        const newTabId = `tab-${Date.now()}-${Math.random()}`;
        setOpenTabs([{ id: newTabId, collectionId: 0, requestId: 0 }]);
        setActiveTabId(newTabId);
      }
    }
  }, [openTabs.length, collections.length, isAuthenticated, showDemo]);
  
  // Показываем Overview если нет активного запроса
  const showOverview = !activeTab || !activeCollection || !activeRequest;

  // Если нет активного запроса, показываем Overview сразу
  if (showOverview) {
    return (
      <div className="bg-white rounded shadow p-0 flex flex-col h-full min-h-[600px]">
        {/* Tab Bar */}
        <div className="relative flex items-center bg-white">
          <div className="flex items-end w-full" style={{ position: 'relative' }}>
            <div
              className="group relative flex items-center px-3 py-2 cursor-default bg-white"
              style={{ 
                fontSize: '12px',
                width: '160px',
                marginRight: 0
              }}
            >
              <span 
                className="truncate"
                style={{ fontSize: '12px', flex: 1, color: '#212121', fontWeight: 500 }}
              >
                Overview
              </span>
              {/* Orange line under tab */}
              <div
                className="absolute left-0 right-0"
                style={{ 
                  bottom: 0, 
                  height: 2, 
                  background: '#f97316', 
                  zIndex: 1
                }}
              />
            </div>
          </div>
          {/* Bottom border */}
          <div
            className="absolute left-0 right-0"
            style={{ 
              bottom: 0, 
              borderBottom: '1px solid #EDEDED',
              zIndex: 0
            }}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-8 py-6">
          <div className="max-w-3xl">
            {/* Introduction */}
            <div className="mb-8">
              <h1 className="text-2xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span>👋</span> Welcome to Supaserver
              </h1>
              <p className="text-gray-600 leading-relaxed">
                Your backend, ready in seconds. Define endpoints, store data, and go live without managing servers.
              </p>
              <p className="text-gray-600 leading-relaxed mt-3">
                Click the <strong>+</strong> button in the sidebar to create your first collection.
              </p>
            </div>

            {/* Getting Started */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <span>🚀</span> Getting started
              </h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-800 mb-1">Collections</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Organize your API endpoints into collections. Each collection can contain multiple requests (GET, POST, PUT, DELETE) that you can execute in real-time.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-800 mb-1">Requests</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Create requests to define your API endpoints. Add request bodies, execute them instantly, and get ready-to-use code snippets for integration.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium text-gray-800 mb-1">Data Storage</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Your data is automatically stored and managed. No database setup required — just send requests and your data persists.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // Цвета методов для дропдауна
  const methodColors: { [key: string]: string } = {
    GET: 'text-green-700',
    POST: 'text-yellow-700',
    PUT: 'text-blue-700',
    PATCH: 'text-purple-700',
    DELETE: 'text-red-800',
    HEAD: 'text-green-700',
    OPTIONS: 'text-pink-700',
  };

  // Hex-цвета для методов в табах (используя CSS переменные)
  const methodHexColors: { [key: string]: string } = {
    GET: 'var(--content-color-success)',
    POST: 'var(--content-color-warning)',
    PUT: 'var(--content-color-info)',
    PATCH: 'var(--content-color-patch-method)',
    DELETE: 'var(--content-color-error)',
    HEAD: 'var(--content-color-success)',
    OPTIONS: 'var(--content-color-options-method)',
  };
  const methodList = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

  const formatUrl = (url: string): string => {
    const formatString = (str: string): string => {
        return str
            .trim()
            .toLowerCase()
            .replace(/\s+/g, '-') // Заменяем пробелы на тире
            .replace(/[^a-z0-9-]/g, '') // Удаляем все символы кроме букв, цифр и тире
            .replace(/-+/g, '-') // Заменяем множественные тире на одно
            .replace(/^-|-$/g, ''); // Удаляем тире в начале и конце
    };

    // Форматируем collection name
    return formatString(url);
  };

  // URL вычисляется динамически на основе name (только если есть activeCollection)
  const demoPrefix = showDemo ? 'demo/' : '';
  const baseUrl = activeCollection ? `${DYNAMIC_API_URL}/${demoPrefix}${formatUrl(activeCollection.name)}` : '';
  // Если есть ID, добавляем его в URL
  const urlWithoutApiKey = requestId 
    ? `${baseUrl}/${requestId}` 
    : baseUrl;
  // API key всегда должен быть в URL, кроме демо-режима
  const url = showDemo 
    ? urlWithoutApiKey 
    : `${urlWithoutApiKey}?apikey=${encodeURIComponent(apiKey || '')}` 

  // Функции генерации кода для разных языков
  const generateCurlCode = () => {
    const method = activeRequest.method;
    const body = requestBody && requestBody !== '{}' ? requestBody.trim() : '';
    let code = `curl -X ${method} "${url}"`;
    if (body && (method !== 'GET' && method !== 'HEAD')) {
      // Для curl нужно минифицировать JSON (убрать переносы строк и лишние пробелы)
      // или использовать реальные переносы строк в многострочном формате
      try {
        // Парсим и минифицируем JSON для curl
        const parsed = JSON.parse(body);
        const minifiedBody = JSON.stringify(parsed);
        // Экранируем одинарные кавычки для shell
        const escapedBody = minifiedBody.replace(/'/g, "'\\''");
        code += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${escapedBody}'`;
      } catch {
        // Если не валидный JSON, используем как есть, но минифицируем
        const minified = body.replace(/\s+/g, ' ').trim();
        const escapedBody = minified.replace(/'/g, "'\\''");
        code += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${escapedBody}'`;
      }
    } else {
      code += ` \\\n  -H "Content-Type: application/json"`;
    }
    return code;
  };

  const generateJsCode = () => {
    const method = activeRequest.method;
    const body = requestBody && requestBody !== '{}' ? requestBody.trim() : null;
    const hasBody = body && method !== 'GET' && method !== 'HEAD';
    if (hasBody) {
      return `fetch('${url}', {
  method: '${method}',
  headers: {
    'Content-Type': 'application/json'
  },
  body: JSON.stringify(${body})
})
  .then(response => response.json())
  .then(data => console.log(data))
  .catch(error => console.error('Error:', error));`;
    } else {
      return `fetch('${url}', {
  method: '${method}',
  headers: {
    'Content-Type': 'application/json'
  }
})
  .then(response => response.json())
  .then(data => console.log(data))
  .catch(error => console.error('Error:', error));`;
    }
  };

  const generateNodejsCode = () => {
    const method = activeRequest.method;
    const body = requestBody && requestBody !== '{}' ? requestBody.trim() : null;
    const hasBody = body && method !== 'GET' && method !== 'HEAD';
    return `const axios = require('axios');

const url = '${url}';
const config = {
  method: '${method}',
  url: url,
  headers: {
    'Content-Type': 'application/json'
  }${hasBody ? `,\n  data: ${body}` : ''}
};

axios(config)
  .then(response => {
    console.log(response.data);
  })
  .catch(error => {
    console.error('Error:', error);
  });`;
  };

  const generatePythonCode = () => {
    const method = activeRequest.method.toLowerCase();
    const body = requestBody && requestBody !== '{}' ? requestBody.trim() : null;
    const hasBody = body && method !== 'get' && method !== 'head';
    return `import requests

url = '${url}'
headers = {
    'Content-Type': 'application/json'
}
${hasBody ? `data = ${body}\n\n` : ''}response = requests.${method}(${hasBody ? 'url, headers=headers, json=data' : 'url, headers=headers'})

print(response.json())`;
  };

  const getCodeForTab = () => {
    switch (activeCodeTab) {
      case 'curl':
        return generateCurlCode();
      case 'js':
        return generateJsCode();
      case 'nodejs':
        return generateNodejsCode();
      case 'python':
        return generatePythonCode();
      default:
        return '';
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Можно добавить уведомление об успешном копировании
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Функция отправки HTTP-запроса
  const handleSend = async () => {
    try {
      // В демо-режиме возвращаем мокированные ответы без реальных запросов
      if (showDemo) {
        // Имитируем небольшую задержку для реалистичности
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Определяем тип запроса по названию или методу
        const requestName = activeRequest.desc?.toLowerCase() || '';
        const requestIndex = activeTab?.requestId ?? 0;
        
        if ((activeRequest.method === 'PUT' || requestName.includes('create-task')) && requestIndex === 0) {
          // Мокированный ответ для PUT (создание задачи)
          const mockResponse = {
            _id: "507f1f77bcf86cd799439011",
            name: "use Supaserver today",
            isDone: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          if (activeTabId) {
            setResponses(prev => ({ ...prev, [activeTabId]: JSON.stringify(mockResponse, null, 2) }));
          }
          return;
        } else if ((activeRequest.method === 'POST' || requestName.includes('make-task-done')) && requestIndex === 1) {
          // Мокированный ответ для POST (изменение статуса задачи на done)
          const mockResponse = {
            _id: "507f1f77bcf86cd799439011",
            name: "use Supaserver today",
            isDone: true,
            createdAt: new Date(Date.now() - 3600000).toISOString(),
            updatedAt: new Date().toISOString()
          };
          if (activeTabId) {
            setResponses(prev => ({ ...prev, [activeTabId]: JSON.stringify(mockResponse, null, 2) }));
          }
          return;
        } else if (activeRequest.method === 'GET') {
          // Мокированный ответ для GET (получение задач)
          const mockResponse = [
            {
              _id: "507f1f77bcf86cd799439011",
              name: "use Supaserver today",
              isDone: false,
              createdAt: new Date(Date.now() - 3600000).toISOString(),
              updatedAt: new Date(Date.now() - 3600000).toISOString()
            },
            {
              _id: "507f1f77bcf86cd799439012",
              name: "Build amazing APIs",
              isDone: true,
              createdAt: new Date(Date.now() - 7200000).toISOString(),
              updatedAt: new Date(Date.now() - 7200000).toISOString()
            }
          ];
          if (activeTabId) {
            setResponses(prev => ({ ...prev, [activeTabId]: JSON.stringify(mockResponse, null, 2) }));
          }
          return;
        }
      }
      
      // Проверяем наличие API key перед отправкой запроса (только если не демо-режим)
      if (!showDemo && !apiKey) {
        const errorMessage = 'API key is missing. Please log out and log in again to get your API key.';
        const errorResponse = {
          error: errorMessage,
          status: 401,
          message: 'Unauthorized: API key is required for all requests.',
          details: 'The API key should be automatically provided when you log in. If you see this error, please log out and log in again.'
        };
        
        if (activeTabId) {
          setResponses(prev => ({ 
            ...prev, 
            [activeTabId]: JSON.stringify(errorResponse, null, 2) 
          }));
        }
        console.error('API key is missing:', errorMessage);
        return;
      }
      
      // Реальные запросы только если не демо-режим
      // Подготавливаем body для запроса
      let requestBodyData = undefined;
      if (activeRequest.method !== 'GET' && activeRequest.method !== 'HEAD') {
        try {
          const parsedBody = requestBody && requestBody !== '{}' ? JSON.parse(requestBody) : {};
          requestBodyData = JSON.stringify(parsedBody);
        } catch {
          requestBodyData = requestBody && requestBody !== '{}' ? requestBody : '{}';
        }
      }
      
      const response = await fetch(url, {
        method: activeRequest.method,
        headers: { 'Content-Type': 'application/json' },
        body: requestBodyData,
      });
      const text = await response.text();
      if (activeTabId) {
        setResponses(prev => ({ ...prev, [activeTabId]: text }));
      }
    } catch (e) {
      if (activeTabId) {
        setResponses(prev => ({ ...prev, [activeTabId]: 'Ошибка при отправке запроса: ' + e }));
      }
    }
  };

  return (
    <>
      <style>{`
        :root {
          --content-color-success: #007F31;
          --content-color-warning: #AD7A03;
          --content-color-info: #0053B8;
          --content-color-patch-method: #623497;
          --content-color-options-method: #A61468;
          --content-color-error: #8E1A10;
          --content-color-secondary: #6B6B6B;
          --border-color-default: #EDEDED;
          --button-main-background-color: #097BED;
        }
      `}</style>
      <div className="bg-white rounded shadow p-0 flex flex-col h-full min-h-[600px]">
        {/* Tabs Bar */}
      <div className="relative flex items-center bg-white">
        <div className="flex items-end w-full" style={{ position: 'relative' }}>
          {openTabs.map((tab) => {
            const collection = collections[tab.collectionId];
            const request = collection?.items[tab.requestId];
            const isActive = tab.id === activeTabId;
            const requestName = request?.desc || 'Untitled Request';
            
            return (
              <>
                <div
                  key={tab.id}
                  className={`group relative flex items-center px-3 py-2 cursor-pointer bg-white transition-all ${isActive ? '' : 'opacity-60 hover:opacity-100'}`}
                  onClick={() => setActiveTabId(tab.id)}
                style={{ 
                  fontSize: '12px',
                  width: '160px',
                  marginRight: 0
                }}
                >
                  {/* Метод GET/POST и т.д. */}
                  <span 
                    className="mr-2"
                    style={{ 
                      fontSize: '9px',
                      fontWeight: 600,
                      letterSpacing: '-0.2px',
                      fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Noto Sans, Ubuntu, Cantarell, Helvetica Neue, Oxygen, Fira Sans, Droid Sans, Helvetica, Arial, sans-serif',
                      lineHeight: '16px',
                      color: methodHexColors[request?.method || 'GET'] || 'var(--content-color-success)'
                    }}
                  >
                    {request?.method || 'GET'}
                  </span>
                  {/* Название запроса */}
                  <span 
                    className="truncate"
                    style={{ fontSize: '12px', flex: 1, color: '#212121' }}
                  >
                    {requestName}
                  </span>
                  {/* Крестик закрытия - появляется при наведении */}
                  <button
                    className="ml-1 w-6 h-6 flex items-center justify-center bg-transparent rounded-md transition-all opacity-0 group-hover:opacity-100 hover:bg-gray-100 focus:outline-none"
                    title="Close tab"
                    tabIndex={-1}
                    onClick={(e) => {
                      e.stopPropagation();
                      closeTab(tab.id);
                    }}
                    style={{ 
                      fontSize: 16, 
                      lineHeight: 1,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: '#9CA3AF'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#212121';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#9CA3AF';
                    }}
                  >
                    ×
                  </button>
                  {/* Оранжевая линия под активной вкладкой */}
                  {isActive && (
                    <div
                      className="absolute left-0 right-0"
                      style={{ 
                        bottom: 0, 
                        height: 2, 
                        background: '#f97316', 
                        zIndex: 1
                      }}
                    />
                  )}
                </div>
                {/* Тонкий разделитель между табами */}
                {tab.id !== openTabs[openTabs.length - 1].id && (
                  <div
                    className="h-full"
                    style={{
                      width: '1px',
                      background: '#e5e7eb'
                    }}
                  />
                )}
              </>
            );
          })}

        </div>
        {/* Общая полоса под всеми табами */}
        <div
          className="absolute left-0 right-0"
          style={{ 
            bottom: 0, 
            borderBottom: '1px solid var(--border-color-default)',
            zIndex: 0
          }}
        />
      </div>
      {/* URL Display (small, below tabs) */}
      <div className="px-6 pt-2 pb-2">
        <UrlDisplay
          collectionName={activeCollection.name}
          requestName={activeRequest.desc || 'Untitled Request'}
          onCollectionChange={(name) => updateCollection(activeTab.collectionId, { name })}
          onRequestChange={(name) => updateRequest(activeTab.collectionId, activeTab.requestId, { desc: name })}
        />
      </div>
      {/* Request Controls */}
      {!apiKey && !showDemo && isAuthenticated ? (
        <div className="flex items-center justify-center px-6 py-4 bg-white">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
        </div>
      ) : (
        <div className="flex items-center px-6 py-4 bg-white">
          <div className="flex items-center w-full max-w-full h-11 border-1 border-gray-300 rounded bg-white relative cursor-default select-none">
            {/* Метод как кнопка-дропдаун */}
            <div className="relative h-full flex items-center" ref={methodDropdownRef}>
              <button
                data-demo="method-dropdown"
                type="button"
                className={`flex items-center gap-3 ml-2 mr-2 h-full px-3 font-bold bg-transparent border-1 cursor-pointer rounded transition-all duration-150 ${methodColors[activeRequest.method] || 'text-gray-700'} border-transparent`}
                onClick={() => setShowMethodDropdown((v) => !v)}
                style={{ 
                  fontSize: '12px',
                  minWidth: 64,
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <span>{activeRequest.method}</span>
                <svg 
                  className="w-4 h-4 text-gray-500" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  style={{ strokeWidth: 2 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {/* Dropdown */}
              {showMethodDropdown && (
                <div className="absolute left-0 top-full mt-2 w-40 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-2" style={{ minWidth: 120 }}>
                  {methodList.map((m) => {
                    const isActive = activeRequest.method === m;
                    return (
                      <div
                        key={m}
                        data-demo={m === 'GET' ? 'get-option' : undefined}
                        className={`flex items-center font-bold cursor-pointer transition-all duration-100 rounded-lg select-none
                          ${methodColors[m] || 'text-gray-700'}
                          ${isActive ? 'bg-gray-100 px-5 py-3 mx-2' : 'px-4 py-2'}
                          ${!isActive ? 'mx-2 hover:bg-gray-100' : ''}
                        `}
                        style={{ fontSize: '12px', minHeight: 32 }}
                        onClick={() => { updateRequest(activeTab.collectionId, activeTab.requestId, { method: m }); setShowMethodDropdown(false); }}
                      >
                        {m}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {/* Короткая вертикальная полоса */}
            <div className="h-6 w-px bg-gray-300 mx-2" />
            {/* Input для url (только для чтения) */}
            <div className="flex-1 relative">
              <input
                className="w-full h-full px-3 border-0 border-gray-300 focus:ring-0 focus:outline-none bg-white placeholder-gray-400 rounded-none py-0"
                placeholder="URL"
                value={url}
                readOnly
                style={{ fontSize: '12px', minWidth: 0, boxShadow: 'none', color: '#212121', cursor: 'default' }}
              />
            </div>
          </div>
          <button
            onClick={() => setShowCodeModal(true)}
            className="h-11 px-4 ml-2 text-gray-700 font-semibold rounded transition-colors border border-gray-300 py-0 cursor-pointer bg-white hover:bg-gray-50"
            style={{ 
              fontSize: '12px',
              minHeight: 40,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            title="Show code"
          >
            Code
          </button>
          <button
            id="send-button"
            data-demo="send-button"
            className="h-11 px-6 ml-2 text-white font-semibold rounded transition-colors border border-l-0 border-gray-300 py-0 cursor-pointer"
            style={{ 
              fontSize: '12px',
              minHeight: 40,
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: 'var(--button-main-background-color)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#0869D1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--button-main-background-color)';
            }}
            onClick={handleSend}
          >
            Send
          </button>
        </div>
      )}

      {/* Inner Tabs (Body and ID) */}
      <div className="flex gap-8 px-6 pt-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveInnerTab('Body')}
            className="relative px-1 pb-2 transition-colors"
            style={{ 
              fontSize: '12px', 
              outline: 'none', 
              background: 'none', 
              border: 'none', 
              color: activeInnerTab === 'Body' ? '#212121' : '#6B6B6B',
              cursor: 'pointer'
            }}
          >
            <span className="relative inline-block">
              Body
              {activeInnerTab === 'Body' && (
                <span className="absolute left-0 right-0 -bottom-2 h-0.5 bg-orange-500 rounded-full w-full" style={{ display: 'block' }}></span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveInnerTab('ID')}
            className="relative px-1 pb-2 transition-colors"
            style={{ 
              fontSize: '12px', 
              outline: 'none', 
              background: 'none', 
              border: 'none', 
              color: activeInnerTab === 'ID' ? '#212121' : '#6B6B6B',
              cursor: 'pointer'
            }}
          >
            <span className="relative inline-block">
              ID
              {activeInnerTab === 'ID' && (
                <span className="absolute left-0 right-0 -bottom-2 h-0.5 bg-orange-500 rounded-full w-full" style={{ display: 'block' }}></span>
              )}
            </span>
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-6 py-4">
        {activeInnerTab === 'ID' ? (
          <div>
            <input
              type="text"
              value={requestId}
              onChange={(e) => {
                if (activeTab) {
                  // Сохраняем в коллекцию
                  updateRequest(activeTab.collectionId, activeTab.requestId, { recordId: e.target.value });
                }
                // Также обновляем локальное состояние для обратной совместимости с демо
                if (activeTabId) {
                  setRequestIds(prev => ({ ...prev, [activeTabId]: e.target.value }));
                }
              }}
              placeholder="Enter ID"
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
              style={{ color: '#212121' }}
            />
          </div>
        ) : (
          <div data-demo="body-editor">
            <JsonEditor
              value={requestBody}
              onChange={(value) => {
                if (activeTab) {
                  // Сохраняем в коллекцию
                  updateRequest(activeTab.collectionId, activeTab.requestId, { body: value });
                }
                // Также обновляем локальное состояние для обратной совместимости с демо
                if (activeTabId) {
                  setRequestBodies(prev => ({ ...prev, [activeTabId]: value }));
                }
              }}
            />
          </div>
        )}
      </div>

      {responseText !== '' && (
        <div className="flex px-6 pb-2 gap-8">
          {/* Response (слева) */}
          <div className="flex-1 min-w-0" data-demo="response-viewer">
            <div className="mb-1 font-semibold" style={{ color: '#212121', fontSize: '12px' }}>Response</div>
            <JsonEditor
              value={(() => {
                try {
                  return JSON.stringify(JSON.parse(responseText), null, 2);
                } catch {
                  return responseText;
                }
              })()}
              onChange={() => {}}
            />
          </div>
        </div>
      )}

      {/* Code Modal */}
      {showCodeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black" style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }} onClick={() => setShowCodeModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl flex flex-col" style={{ height: '420px', marginTop: '-30vh' }} onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg font-semibold" style={{ color: '#212121' }}>Code Snippet</h2>
              <button
                onClick={() => setShowCodeModal(false)}
                className="text-gray-500 hover:text-gray-700"
                style={{ fontSize: '24px', lineHeight: 1, cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 px-6 flex-shrink-0">
              {(['curl', 'js', 'nodejs', 'python'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveCodeTab(tab)}
                  className="px-4 py-3 text-sm font-medium transition-colors relative"
                  style={{
                    color: activeCodeTab === tab ? '#212121' : '#6B6B6B',
                    borderBottom: activeCodeTab === tab ? '2px solid #f97316' : '2px solid transparent',
                    marginBottom: '-2px'
                  }}
                >
                  {tab === 'js' ? 'JavaScript' : tab === 'nodejs' ? 'Node.js' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Code Content */}
            <div className="flex-1 overflow-auto px-6 py-4" style={{ minHeight: 0 }}>
              <div className="relative">
                <pre className="bg-gray-50 p-4 rounded text-sm overflow-x-auto" style={{ fontFamily: 'monospace', color: '#212121' }}>
                  <code>{getCodeForTab()}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(getCodeForTab())}
                  className="absolute top-2 right-2 px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-xs font-medium transition-colors"
                  style={{ color: '#212121', cursor: 'pointer' }}
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
} 